import React from 'react';
import { cn } from '@/lib/utils/cn';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular';
  shimmer?: boolean;
}

interface SkeletonTextProps {
  lines?: number;
  className?: string;
  shimmer?: boolean;
}

interface SkeletonCardProps {
  className?: string;
  shimmer?: boolean;
}

interface SkeletonListProps {
  items?: number;
  className?: string;
  shimmer?: boolean;
}

interface SkeletonArticleProps {
  className?: string;
  shimmer?: boolean;
}

export function Skeleton({ className, variant = 'text', shimmer = false, ...props }: SkeletonProps) {
  return (
    <div
      className={cn(
        'rounded-3 bg-surface-tertiary',
        shimmer ? 'animate-shimmer' : 'animate-pulse',
        variant === 'text' && 'h-4 w-full',
        variant === 'circular' && 'h-10 w-10 rounded-full',
        variant === 'rectangular' && 'h-20 w-full',
        className
      )}
      {...props}
    />
  );
}
Skeleton.displayName = "Skeleton";

export function SkeletonText({ lines = 3, className, shimmer }: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} shimmer={shimmer} className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`} />
      ))}
    </div>
  );
}
SkeletonText.displayName = "SkeletonText";

export function SkeletonCard({ className, shimmer }: SkeletonCardProps) {
  return (
    <div className={cn('rounded-4 border border-border bg-surface p-6 shadow-sm', className)}>
      <SkeletonText lines={3} shimmer={shimmer} />
    </div>
  );
}
SkeletonCard.displayName = "SkeletonCard";

export function SkeletonList({ items = 3, className, shimmer }: SkeletonListProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton variant="circular" shimmer={shimmer} />
          <div className="flex-1 space-y-2">
            <Skeleton shimmer={shimmer} className="h-4 w-3/4" />
            <Skeleton shimmer={shimmer} className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
SkeletonList.displayName = "SkeletonList";

export function SkeletonArticle({ className, shimmer }: SkeletonArticleProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <Skeleton variant="rectangular" shimmer={shimmer} className="aspect-[16/9] rounded-5" />
      <Skeleton shimmer={shimmer} className="h-4 w-1/4" />
      <Skeleton shimmer={shimmer} className="h-6 w-3/4" />
      <SkeletonText lines={2} shimmer={shimmer} />
    </div>
  );
}
SkeletonArticle.displayName = "SkeletonArticle";

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
  shimmer?: boolean;
}

export function SkeletonTable({ rows = 5, columns = 4, className, shimmer }: SkeletonTableProps) {
  return (
    <div className={cn('rounded-4 border border-border bg-surface overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i} className="p-4 text-left">
                  <Skeleton variant="text" shimmer={shimmer} className="h-4 w-full" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, row) => (
              <tr key={row} className="border-b border-border/50 last:border-0">
                {Array.from({ length: columns }).map((_, col) => (
                  <td key={col} className="p-4">
                    <Skeleton variant="text" shimmer={shimmer} className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
SkeletonTable.displayName = "SkeletonTable";
